import type { CategoryLink, SalonProfile } from '@/types'

import Link from 'next/link'
import {
    Clock,
    Facebook,
    Instagram,
    Mail,
    MapPin,
    Phone,
    Youtube
} from 'lucide-react'
import { Logo } from '@/components/site/logo'
import { cms } from '@/lib/cms'
import { telLink } from '@/lib/phone'
import { formatHours, formatOpenDays } from '@/lib/salon'
import { PUBLIC_NAV } from '@/constants/site'

interface SiteFooterProps {
    profile: SalonProfile
    categories: Array<CategoryLink>
}

export const SiteFooter = ({ profile, categories }: SiteFooterProps) => {
    const socials = [
        { href: profile.instagram_url, label: 'Instagram', icon: Instagram },
        { href: profile.facebook_url, label: 'Facebook', icon: Facebook },
        { href: profile.youtube_url, label: 'YouTube', icon: Youtube }
    ].filter(item => Boolean(item.href))

    return (
        <footer className='relative overflow-hidden bg-navy-950 text-cream-100'>
            <div aria-hidden='true' className='gold-rule absolute inset-x-0 top-0 opacity-60' />
            <div aria-hidden='true' className='pointer-events-none absolute -top-40 -right-40 size-[28rem] rounded-full bg-emerald-700/20 blur-3xl' />

            <div className='relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-12 lg:px-8 lg:py-20'>
                <div className='lg:col-span-4'>
                    <Logo profileId={profile.id} shortName={profile.short_name} tone='dark' />
                    {profile.tagline && (
                        <p data-mdlfy-cms={cms('salon_profile', profile.id, 'tagline')} className='mt-6 max-w-sm text-sm leading-relaxed text-cream-200/75'>
                            {profile.tagline}
                        </p>
                    )}
                    {socials.length > 0 && (
                        <div className='mt-6 flex gap-3'>
                            {socials.map(({ href, label, icon: Icon }) => (
                                <a
                                    key={label}
                                    href={href ?? '#'}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    aria-label={label}
                                    className='flex size-10 items-center justify-center rounded-full border border-cream-50/15 text-cream-100 transition hover:border-gold-300 hover:text-gold-200'
                                >
                                    <Icon className='size-4' />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                <div className='lg:col-span-2'>
                    <h2 className='text-xs font-semibold tracking-[0.24em] text-gold-300 uppercase'>Explore</h2>
                    <ul className='mt-5 space-y-3 text-sm'>
                        {PUBLIC_NAV.map(item => (
                            <li key={item.href}>
                                <Link href={item.href} className='text-cream-200/80 transition hover:text-gold-200'>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link href='/book' className='text-cream-200/80 transition hover:text-gold-200'>
                                Book appointment
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className='lg:col-span-2'>
                    <h2 className='text-xs font-semibold tracking-[0.24em] text-gold-300 uppercase'>Services</h2>
                    <ul className='mt-5 space-y-3 text-sm'>
                        {categories.map(category => (
                            <li key={category.id}>
                                <Link href={`/services#${category.slug}`} className='text-cream-200/80 transition hover:text-gold-200'>
                                    <span data-mdlfy-cms={cms('service_categories', category.id, 'name')}>
                                        {category.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='lg:col-span-4'>
                    <h2 className='text-xs font-semibold tracking-[0.24em] text-gold-300 uppercase'>Visit the studio</h2>
                    <ul className='mt-5 space-y-4 text-sm text-cream-200/80'>
                        <li className='flex gap-3'>
                            <MapPin className='mt-0.5 size-4 shrink-0 text-gold-300' />
                            <address className='not-italic leading-relaxed'>
                                {profile.address && <span data-mdlfy-cms={cms('salon_profile', profile.id, 'address')}>{profile.address}</span>}
                                {profile.area && (
                                    <>
                                        {', '}
                                        <span data-mdlfy-cms={cms('salon_profile', profile.id, 'area')}>{profile.area}</span>
                                    </>
                                )}
                                <br />
                                {profile.city && <span data-mdlfy-cms={cms('salon_profile', profile.id, 'city')}>{profile.city}</span>}
                                {profile.state && (
                                    <>
                                        {', '}
                                        <span data-mdlfy-cms={cms('salon_profile', profile.id, 'state')}>{profile.state}</span>
                                    </>
                                )}
                                {profile.pincode && (
                                    <>
                                        {' '}
                                        <span data-mdlfy-cms={cms('salon_profile', profile.id, 'pincode')}>{profile.pincode}</span>
                                    </>
                                )}
                            </address>
                        </li>
                        <li className='flex gap-3'>
                            <Clock className='mt-0.5 size-4 shrink-0 text-gold-300' />
                            <span>
                                {formatOpenDays(profile.closed_days)}
                                <br />
                                {formatHours(profile)}
                            </span>
                        </li>
                        {profile.phone && (
                            <li className='flex gap-3'>
                                <Phone className='mt-0.5 size-4 shrink-0 text-gold-300' />
                                <a href={telLink(profile.phone)} className='transition hover:text-gold-200'>
                                    <span data-mdlfy-cms={cms('salon_profile', profile.id, 'phone')}>{profile.phone}</span>
                                </a>
                            </li>
                        )}
                        {profile.email && (
                            <li className='flex gap-3'>
                                <Mail className='mt-0.5 size-4 shrink-0 text-gold-300' />
                                <a href={`mailto:${profile.email}`} className='break-all transition hover:text-gold-200'>
                                    <span data-mdlfy-cms={cms('salon_profile', profile.id, 'email')}>{profile.email}</span>
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className='relative border-t border-cream-50/10'>
                <div className='mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-cream-200/60 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8'>
                    <p>
                        <span suppressHydrationWarning>{`© ${new Date().getFullYear()} `}</span>
                        <span data-mdlfy-cms={cms('salon_profile', profile.id, 'name')}>{profile.name}</span>
                        . All rights reserved.
                    </p>
                    <div className='flex gap-5'>
                        <Link href='/contact' className='transition hover:text-gold-200'>Help and FAQs</Link>
                        <Link href='/admin' className='transition hover:text-gold-200'>Owner login</Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}
